import {
  CASE_CREATE_GROUP_BEGIN,
  CASE_CREATE_GROUP_SUCCESS,
  CASE_CREATE_GROUP_FAILURE,
  CASE_CREATE_GROUP_DISMISS_FEEDBACK,
} from './constants';
import { useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import api from 'common/api';
import { withCurrentCaseId } from '../../../common/selectors';
import { addItemImmutable } from 'utils/arrays';

export const createGroup = createPromiseAction(CASE_CREATE_GROUP_BEGIN);

export function dismissCreateGroupFeedback() {
  return {
    type: CASE_CREATE_GROUP_DISMISS_FEEDBACK,
  };
}

export function* doCreateGroup(action) {
  const { caseId, group } = action.payload;
  let res = yield call(api.post, `/cases/${caseId}/groups`, group);

  if (res && res.error) {
    yield put({
      type: CASE_CREATE_GROUP_FAILURE,
      feedback: { message: 'feedback.createGroupFailure', error: res.error, retryAction: action },
    });
    return yield call(rejectPromiseAction, action, res.error);
  }

  yield put({
    type: CASE_CREATE_GROUP_SUCCESS,
    data: { group: res },
  });
  yield call(resolvePromiseAction, action, res);
}

export function* watchCreateGroup() {
  yield takeLatest(createGroup, withCurrentCaseId(doCreateGroup));
}

export function useCreateGroup() {
  const dispatch = useDispatch();

  const { createGroupPending, createGroupFeedback } = useSelector(
    (state) => ({
      createGroupPending: state.case.createGroupPending,
      createGroupFeedback: state.case.createGroupFeedback,
    }),
    shallowEqual,
  );

  const boundAction = useCallback(
    (...args) => {
      return dispatch(createGroup(...args));
    },
    [dispatch],
  );

  const boundDismissFeedback = useCallback(() => {
    return dispatch(dismissCreateGroupFeedback());
  }, [dispatch]);

  return {
    createGroup: boundAction,
    createGroupPending,
    createGroupFeedback,
    dismissCreateGroupFeedback: boundDismissFeedback,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case CASE_CREATE_GROUP_BEGIN + '.TRIGGER':
      // Just after a request is sent
      return {
        ...state,
        createGroupPending: true,
        createGroupFeedback: null,
      };

    case CASE_CREATE_GROUP_SUCCESS:
      // The request is success
      return {
        ...state,
        createGroupPending: false,
        createGroupFeedback: action.feedback,
        groups: addItemImmutable(state.groups, action.data.group),
      };

    case CASE_CREATE_GROUP_FAILURE:
      // The request is failed
      return {
        ...state,
        createGroupPending: false,
        createGroupFeedback: action.feedback,
      };

    case CASE_CREATE_GROUP_DISMISS_FEEDBACK:
      // Dismiss the request failure error
      return {
        ...state,
        createGroupFeedback: null,
      };

    default:
      return state;
  }
}
